import { create } from 'zustand';
import type { DrawingSummary, MissedItem } from '../types';

/** 提示条上的可选操作按钮（例：删除后的「撤销」）。 */
export interface ToastAction {
  label: string;
  run: () => void;
}

export interface ToastState {
  msg: string;
  type: 'info' | 'success' | 'error';
  action?: ToastAction;
}

interface UIState {
  selectedDrawingId: number | null;
  selectedDrawingSummary: DrawingSummary | null;
  selectedFeaturePk: number | null;     // 画布<->表格联动
  canvasPageIndex: number;               // 当前画布页码
  toast: ToastState | null;

  balloonAddMode: boolean;               // v1 手动添加气泡：开启后点空白处放置
  setBalloonAddMode: (on: boolean) => void;

  // 框选识别（品质大师式）：开启后在图纸上拖拽框选区域，只识别该区域
  regionSelectMode: boolean;
  setRegionSelectMode: (on: boolean) => void;

  // Task #55：疑似漏检定位
  locatedMissed: MissedItem | null;       // 当前高亮/定位的疑似漏检项
  locatedMissedSeq: number;               // 每次定位自增，驱动 Canvas 重复居中

  selectDrawing: (id: number | null, summary?: DrawingSummary | null) => void;
  selectFeature: (pk: number | null) => void;
  setCanvasPage: (idx: number) => void;
  showToast: (msg: string, type?: ToastState['type'],
              action?: ToastAction) => void;
  clearToast: () => void;
  setLocatedMissed: (item: MissedItem | null) => void;
  clearLocatedMissed: () => void;
}

// 普通提示的存活时间；带操作按钮的延长 —— 一秒钟就消失的「撤销」等于没有。
const TOAST_MS = 2800;
const TOAST_ACTION_MS = 8000;

let _toastTimer: ReturnType<typeof setTimeout> | null = null;

export const useUI = create<UIState>((set) => ({
  selectedDrawingId: null,
  selectedDrawingSummary: null,
  selectedFeaturePk: null,
  canvasPageIndex: 0,
  toast: null,
  balloonAddMode: false,
  regionSelectMode: false,
  locatedMissed: null,
  locatedMissedSeq: 0,

  selectDrawing: (id, summary = null) =>
    set({ selectedDrawingId: id, selectedDrawingSummary: summary,
          selectedFeaturePk: null, canvasPageIndex: 0, locatedMissed: null,
          balloonAddMode: false, regionSelectMode: false }),
  selectFeature: (pk) => set({ selectedFeaturePk: pk }),
  setCanvasPage: (idx) => set({ canvasPageIndex: idx }),
  showToast: (msg, type = 'info', action) => {
    if (_toastTimer) clearTimeout(_toastTimer);
    set({ toast: { msg, type, action } });
    _toastTimer = setTimeout(
      () => set({ toast: null }),
      action ? TOAST_ACTION_MS : TOAST_MS);
  },
  clearToast: () => {
    if (_toastTimer) clearTimeout(_toastTimer);
    set({ toast: null });
  },
  setBalloonAddMode: (on) =>
    set({ balloonAddMode: on, ...(on ? { regionSelectMode: false } : {}) }),
  setRegionSelectMode: (on) =>
    set({ regionSelectMode: on, ...(on ? { balloonAddMode: false } : {}) }),
  setLocatedMissed: (item) =>
    set((s) => ({ locatedMissed: item, locatedMissedSeq: item ? s.locatedMissedSeq + 1 : s.locatedMissedSeq })),
  clearLocatedMissed: () => set({ locatedMissed: null }),
}));
